/**
 * Predefined Layout Templates for Flow2
 *
 * This file contains the built-in layout templates that the AI compiler uses
 * to position content blocks on screens.
 *
 * @packageDocumentation
 * @module ai/layouts
 */

import {
  LayoutTemplate,
  createLayoutTemplate,
  createSlot,
  DEFAULT_VIEW_SIZE,
  LAYOUT_PADDING,
} from '../schemas/LayoutTemplate';

const { width: W, height: H } = DEFAULT_VIEW_SIZE;
const P = LAYOUT_PADDING.screen;

/**
 * Title Centered Layout
 * Big centered title with optional subtitle below
 * Optimized for widescreen with larger fonts for impact
 */
export const LAYOUT_TITLE_CENTERED: LayoutTemplate = createLayoutTemplate(
  'title-centered',
  'Title Centered',
  'title',
  [
    createSlot(
      'main-title',
      'Main Title',
      ['title'],
      { x: P * 2, y: H * 0.32, width: W - P * 4, height: 160, unit: 'px' },
      {
        required: true,
        priority: 10,
        defaultStyle: {
          fontSize: 96,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2, autoFit: true },
      }
    ),
    createSlot(
      'subtitle',
      'Subtitle',
      ['subtitle', 'body'],
      { x: P * 2, y: H * 0.55, width: W - P * 4, height: 80, unit: 'px' },
      {
        priority: 5,
        defaultStyle: {
          fontSize: 42,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Light',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Large centered title with optional subtitle',
    selectionRules: {
      preferredScreenTypes: ['title-slide', 'section-header', 'closing'],
      minBlocks: 1,
      maxBlocks: 3,
      requiredBlockTypes: ['title'],
      scoreBonus: 2,
    },
    tags: ['title', 'intro', 'centered'],
  }
);

/**
 * Title with Body Layout
 * Title at top, body text below
 * Optimized for widescreen with larger fonts
 */
export const LAYOUT_TITLE_BODY: LayoutTemplate = createLayoutTemplate(
  'title-body',
  'Title with Body',
  'content',
  [
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: P, y: P, width: W - P * 2, height: 100, unit: 'px' },
      {
        required: true,
        priority: 10,
        defaultStyle: {
          fontSize: 64,
          textAlignment: 'left',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'body',
      'Body Text',
      ['body', 'bullet-list', 'numbered-list', 'stat', 'chart'],
      { x: P, y: 160, width: W - P * 2, height: H - 200, unit: 'px' },
      {
        priority: 8,
        defaultStyle: {
          fontSize: 36,
          textAlignment: 'left',
          fontName: 'HelveticaNeue',
          lineHeight: 1.5,
        },
        constraints: { maxLines: 12 },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Title at top with body text below',
    selectionRules: {
      preferredScreenTypes: ['content'],
      minBlocks: 1,
      maxBlocks: 3,
    },
    tags: ['content', 'text', 'standard'],
  }
);

/**
 * Two Column Layout
 * Title at top, two columns of content below
 * Optimized for widescreen - great for utilizing horizontal space
 */
export const LAYOUT_TWO_COLUMN: LayoutTemplate = createLayoutTemplate(
  'two-column',
  'Two Column',
  'comparison',
  [
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: P, y: P, width: W - P * 2, height: 90, unit: 'px' },
      {
        required: true,
        priority: 10,
        defaultStyle: {
          fontSize: 64,
          textAlignment: 'left',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'left-column',
      'Left Column',
      ['body', 'bullet-list', 'numbered-list', 'callout', 'stat', 'chart'],
      { x: P, y: 150, width: (W - P * 3) / 2, height: H - 190, unit: 'px' },
      {
        priority: 8,
        defaultStyle: {
          fontSize: 32,
          textAlignment: 'left',
          fontName: 'HelveticaNeue',
        },
        constraints: { maxLines: 10 },
      }
    ),
    createSlot(
      'right-column',
      'Right Column',
      ['body', 'bullet-list', 'numbered-list', 'callout', 'stat', 'chart'],
      {
        x: P + (W - P * 3) / 2 + P,
        y: 150,
        width: (W - P * 3) / 2,
        height: H - 190,
        unit: 'px',
      },
      {
        priority: 7,
        defaultStyle: {
          fontSize: 32,
          textAlignment: 'left',
          fontName: 'HelveticaNeue',
        },
        constraints: { maxLines: 10 },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Two-column layout for comparisons or parallel content',
    selectionRules: {
      preferredScreenTypes: ['two-column', 'comparison', 'content'],
      minBlocks: 2,
      maxBlocks: 4,
    },
    tags: ['columns', 'comparison', 'split'],
  }
);

/**
 * Image Left Layout
 * Image on left, text content on right
 * Optimized for widescreen - 50/50 split for balanced visual
 */
export const LAYOUT_IMAGE_LEFT: LayoutTemplate = createLayoutTemplate(
  'image-left',
  'Image Left',
  'media',
  [
    createSlot(
      'image',
      'Image',
      ['image-placeholder'],
      { x: 0, y: 0, width: W * 0.5, height: H, unit: 'px' },
      {
        required: true,
        priority: 10,
        zIndex: 0,
      }
    ),
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      {
        x: W * 0.5 + P,
        y: P * 2,
        width: W * 0.5 - P * 2,
        height: 100,
        unit: 'px',
      },
      {
        priority: 9,
        defaultStyle: {
          fontSize: 56,
          textAlignment: 'left',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'body',
      'Body Text',
      ['body', 'bullet-list', 'numbered-list', 'caption', 'stat', 'chart'],
      {
        x: W * 0.5 + P,
        y: 150,
        width: W * 0.5 - P * 2,
        height: H - 190,
        unit: 'px',
      },
      {
        priority: 7,
        defaultStyle: {
          fontSize: 32,
          textAlignment: 'left',
          fontName: 'HelveticaNeue',
          lineHeight: 1.5,
        },
        constraints: { maxLines: 12 },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Image on left with title and body on right',
    selectionRules: {
      preferredScreenTypes: ['image-focus', 'content'],
      minBlocks: 1,
      maxBlocks: 4,
      requiredBlockTypes: ['image-placeholder'],
    },
    tags: ['image', 'media', 'split'],
  }
);

/**
 * Image Right Layout
 * Text content on left, image on right
 * Optimized for widescreen - 50/50 split for balanced visual
 */
export const LAYOUT_IMAGE_RIGHT: LayoutTemplate = createLayoutTemplate(
  'image-right',
  'Image Right',
  'media',
  [
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: P, y: P * 2, width: W * 0.5 - P * 2, height: 100, unit: 'px' },
      {
        priority: 9,
        defaultStyle: {
          fontSize: 56,
          textAlignment: 'left',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'body',
      'Body Text',
      ['body', 'bullet-list', 'numbered-list', 'caption', 'stat', 'chart'],
      { x: P, y: 150, width: W * 0.5 - P * 2, height: H - 190, unit: 'px' },
      {
        priority: 7,
        defaultStyle: {
          fontSize: 32,
          textAlignment: 'left',
          fontName: 'HelveticaNeue',
          lineHeight: 1.5,
        },
        constraints: { maxLines: 12 },
      }
    ),
    createSlot(
      'image',
      'Image',
      ['image-placeholder'],
      {
        x: W * 0.5,
        y: 0,
        width: W * 0.5,
        height: H,
        unit: 'px',
      },
      {
        required: true,
        priority: 10,
        zIndex: 0,
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Title and body on left with image on right',
    selectionRules: {
      preferredScreenTypes: ['image-focus', 'content'],
      minBlocks: 1,
      maxBlocks: 4,
      requiredBlockTypes: ['image-placeholder'],
    },
    tags: ['image', 'media', 'split'],
  }
);

/**
 * Full Image Layout
 * Full-bleed image with overlay text
 * Perfect for widescreen hero slides
 */
export const LAYOUT_FULL_IMAGE: LayoutTemplate = createLayoutTemplate(
  'full-image',
  'Full Image',
  'media',
  [
    createSlot(
      'background-image',
      'Background Image',
      ['image-placeholder'],
      { x: 0, y: 0, width: W, height: H, unit: 'px' },
      {
        required: true,
        priority: 10,
        zIndex: 0,
      }
    ),
    createSlot(
      'title',
      'Title Overlay',
      ['title', 'subtitle'],
      { x: P * 2, y: H * 0.55, width: W - P * 4, height: 120, unit: 'px' },
      {
        priority: 8,
        defaultStyle: {
          fontSize: 72,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
          textColor: '#FFFFFF',
        },
        constraints: { maxLines: 2 },
        zIndex: 2,
      }
    ),
    createSlot(
      'subtitle',
      'Subtitle Overlay',
      ['subtitle', 'body', 'caption'],
      { x: P * 2, y: H * 0.72, width: W - P * 4, height: 60, unit: 'px' },
      {
        priority: 5,
        defaultStyle: {
          fontSize: 36,
          textAlignment: 'center',
          fontName: 'HelveticaNeue',
          textColor: '#FFFFFF',
        },
        constraints: { maxLines: 2 },
        zIndex: 2,
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Full-bleed image with text overlay',
    selectionRules: {
      preferredScreenTypes: ['image-focus', 'title-slide', 'section-header'],
      minBlocks: 1,
      maxBlocks: 4,
      requiredBlockTypes: ['image-placeholder'],
      scoreBonus: 1,
    },
    tags: ['image', 'fullscreen', 'overlay'],
  }
);

/**
 * Bullets Layout
 * Title at top with bullet list below
 */
export const LAYOUT_BULLETS: LayoutTemplate = createLayoutTemplate(
  'bullets',
  'Bullet List',
  'list',
  [
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: P, y: P, width: W - P * 2, height: 70, unit: 'px' },
      {
        required: true,
        priority: 10,
        defaultStyle: {
          fontSize: 52,
          textAlignment: 'left',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'bullets',
      'Bullet Points',
      ['bullet-list', 'numbered-list'],
      { x: P, y: 130, width: W - P * 2, height: H - 170, unit: 'px' },
      {
        required: true,
        priority: 9,
        defaultStyle: {
          fontSize: 28,
          textAlignment: 'left',
          fontName: 'HelveticaNeue',
          lineHeight: 1.6,
        },
        constraints: { maxLines: 10 },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Title with bullet points',
    selectionRules: {
      preferredScreenTypes: ['content'],
      minBlocks: 2,
      maxBlocks: 3,
      requiredBlockTypes: ['bullet-list'],
      scoreBonus: 1,
    },
    tags: ['list', 'bullets', 'points'],
  }
);

/**
 * Quote Layout
 * Centered quote with attribution
 */
export const LAYOUT_QUOTE: LayoutTemplate = createLayoutTemplate(
  'quote',
  'Quote',
  'quote',
  [
    createSlot(
      'quote',
      'Quote Text',
      ['quote'],
      { x: P * 2, y: H * 0.25, width: W - P * 4, height: 200, unit: 'px' },
      {
        required: true,
        priority: 10,
        defaultStyle: {
          fontSize: 48,
          textAlignment: 'center',
          fontName: 'Georgia-Italic',
          lineHeight: 1.4,
        },
        constraints: { maxLines: 5 },
      }
    ),
    createSlot(
      'attribution',
      'Attribution',
      ['body', 'caption', 'stat', 'chart'],
      { x: P * 2, y: H * 0.65, width: W - P * 4, height: 60, unit: 'px' },
      {
        priority: 5,
        defaultStyle: {
          fontSize: 30,
          textAlignment: 'center',
          fontName: 'HelveticaNeue',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Centered quote with attribution',
    selectionRules: {
      preferredScreenTypes: ['quote-slide', 'content'],
      minBlocks: 1,
      maxBlocks: 3,
      requiredBlockTypes: ['quote'],
      scoreBonus: 3,
    },
    tags: ['quote', 'testimonial', 'citation'],
  }
);

/**
 * Three Column Layout
 * Title at top with three equal columns
 */
export const LAYOUT_THREE_COLUMN: LayoutTemplate = createLayoutTemplate(
  'three-column',
  'Three Column',
  'comparison',
  [
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: P, y: P, width: W - P * 2, height: 60, unit: 'px' },
      {
        priority: 10,
        defaultStyle: {
          fontSize: 48,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'column-1',
      'Column 1',
      ['body', 'bullet-list', 'callout', 'stat', 'chart'],
      {
        x: P,
        y: 120,
        width: (W - P * 4) / 3,
        height: H - 160,
        unit: 'px',
      },
      {
        priority: 8,
        defaultStyle: {
          fontSize: 28,
          textAlignment: 'center',
          fontName: 'HelveticaNeue',
        },
        constraints: { maxLines: 8 },
      }
    ),
    createSlot(
      'column-2',
      'Column 2',
      ['body', 'bullet-list', 'callout', 'stat', 'chart'],
      {
        x: P + (W - P * 4) / 3 + P,
        y: 120,
        width: (W - P * 4) / 3,
        height: H - 160,
        unit: 'px',
      },
      {
        priority: 7,
        defaultStyle: {
          fontSize: 28,
          textAlignment: 'center',
          fontName: 'HelveticaNeue',
        },
        constraints: { maxLines: 8 },
      }
    ),
    createSlot(
      'column-3',
      'Column 3',
      ['body', 'bullet-list', 'callout', 'stat', 'chart'],
      {
        x: P + ((W - P * 4) / 3 + P) * 2,
        y: 120,
        width: (W - P * 4) / 3,
        height: H - 160,
        unit: 'px',
      },
      {
        priority: 6,
        defaultStyle: {
          fontSize: 28,
          textAlignment: 'center',
          fontName: 'HelveticaNeue',
        },
        constraints: { maxLines: 8 },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Three equal columns for comparing options',
    selectionRules: {
      preferredScreenTypes: ['comparison', 'content'],
      minBlocks: 3,
      maxBlocks: 5,
    },
    tags: ['columns', 'comparison', 'features'],
  }
);

/**
 * Closing Slide Layout
 * Thank you or call-to-action slide
 */
export const LAYOUT_CLOSING: LayoutTemplate = createLayoutTemplate(
  'closing',
  'Closing Slide',
  'closing',
  [
    createSlot(
      'main-message',
      'Main Message',
      ['title'],
      { x: P, y: H * 0.3, width: W - P * 2, height: 100, unit: 'px' },
      {
        required: true,
        priority: 10,
        defaultStyle: {
          fontSize: 64,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'subtitle',
      'Subtitle',
      ['subtitle', 'body'],
      { x: P, y: H * 0.5, width: W - P * 2, height: 60, unit: 'px' },
      {
        priority: 5,
        defaultStyle: {
          fontSize: 28,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Light',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'contact',
      'Contact Info',
      ['body', 'caption', 'stat', 'chart'],
      { x: P, y: H * 0.7, width: W - P * 2, height: 80, unit: 'px' },
      {
        priority: 3,
        defaultStyle: {
          fontSize: 28,
          textAlignment: 'center',
          fontName: 'HelveticaNeue',
        },
        constraints: { maxLines: 3 },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Closing slide with thank you or call-to-action',
    selectionRules: {
      preferredScreenTypes: ['closing'],
      minBlocks: 1,
      maxBlocks: 4,
      scoreBonus: 3,
    },
    tags: ['closing', 'thankyou', 'cta'],
  }
);

/**
 * Vertical Mobile Layout (9:16 aspect ratio)
 * Optimized for mobile viewing - stacked content
 */
const VERTICAL_WIDTH = 576;
const VERTICAL_HEIGHT = 1024;
const VP = 40; // Vertical padding

export const LAYOUT_VERTICAL_MOBILE: LayoutTemplate = createLayoutTemplate(
  'vertical-mobile',
  'Vertical Mobile',
  'title',
  [
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: VP, y: VP * 2, width: VERTICAL_WIDTH - VP * 2, height: 120, unit: 'px' },
      {
        required: true,
        priority: 10,
        defaultStyle: {
          fontSize: 48,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 3 },
      }
    ),
    createSlot(
      'body',
      'Body Content',
      ['body', 'bullet-list', 'numbered-list', 'stat', 'chart'],
      { x: VP, y: 200, width: VERTICAL_WIDTH - VP * 2, height: 500, unit: 'px' },
      {
        priority: 8,
        defaultStyle: {
          fontSize: 32,
          textAlignment: 'center',
          fontName: 'HelveticaNeue',
          lineHeight: 1.6,
        },
        constraints: { maxLines: 12 },
      }
    ),
    createSlot(
      'cta',
      'Call to Action',
      ['callout', 'body'],
      { x: VP, y: 750, width: VERTICAL_WIDTH - VP * 2, height: 100, unit: 'px' },
      {
        priority: 5,
        defaultStyle: {
          fontSize: 28,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Medium',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Vertical 9:16 layout optimized for mobile viewing',
    selectionRules: {
      preferredScreenTypes: ['title-slide', 'content'],
      minBlocks: 1,
      maxBlocks: 4,
    },
    tags: ['vertical', 'mobile', '9:16', 'portrait'],
    viewSize: { width: VERTICAL_WIDTH, height: VERTICAL_HEIGHT },
  }
);

/**
 * Image Top Layout
 * Wide image across top, title and text below
 */
export const LAYOUT_IMAGE_TOP: LayoutTemplate = createLayoutTemplate(
  'image-top',
  'Image Top',
  'media',
  [
    createSlot(
      'image',
      'Image',
      ['image-placeholder'],
      { x: 0, y: 0, width: W, height: H * 0.5, unit: 'px' },
      {
        required: true,
        priority: 10,
        zIndex: 0,
      }
    ),
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: P, y: H * 0.53, width: W - P * 2, height: 70, unit: 'px' },
      {
        priority: 9,
        defaultStyle: {
          fontSize: 48,
          textAlignment: 'left',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'body',
      'Body Text',
      ['body', 'bullet-list', 'numbered-list', 'caption', 'stat', 'chart'],
      { x: P, y: H * 0.63, width: W - P * 2, height: H * 0.33, unit: 'px' },
      {
        priority: 7,
        defaultStyle: {
          fontSize: 28,
          textAlignment: 'left',
          fontName: 'HelveticaNeue',
          lineHeight: 1.4,
        },
        constraints: { maxLines: 8 },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Wide image across top with title and body below',
    selectionRules: {
      preferredScreenTypes: ['image-focus', 'content'],
      minBlocks: 1,
      maxBlocks: 4,
      requiredBlockTypes: ['image-placeholder'],
    },
    tags: ['image', 'media', 'top'],
  }
);

/**
 * Two Images Layout
 * Two images side by side with title above
 */
export const LAYOUT_TWO_IMAGES: LayoutTemplate = createLayoutTemplate(
  'two-images',
  'Two Images',
  'media',
  [
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: P, y: P, width: W - P * 2, height: 60, unit: 'px' },
      {
        priority: 10,
        defaultStyle: {
          fontSize: 48,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'image-left',
      'Left Image',
      ['image-placeholder'],
      { x: P, y: 120, width: (W - P * 3) / 2, height: H - 200, unit: 'px' },
      {
        required: true,
        priority: 9,
        zIndex: 0,
      }
    ),
    createSlot(
      'image-right',
      'Right Image',
      ['image-placeholder'],
      {
        x: P + (W - P * 3) / 2 + P,
        y: 120,
        width: (W - P * 3) / 2,
        height: H - 200,
        unit: 'px',
      },
      {
        priority: 8,
        zIndex: 0,
      }
    ),
    createSlot(
      'caption',
      'Caption',
      ['body', 'caption', 'stat', 'chart'],
      { x: P, y: H - 60, width: W - P * 2, height: 40, unit: 'px' },
      {
        priority: 3,
        defaultStyle: {
          fontSize: 22,
          textAlignment: 'center',
          fontName: 'HelveticaNeue',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Two images side by side with title and optional caption',
    selectionRules: {
      preferredScreenTypes: ['comparison', 'image-focus'],
      minBlocks: 2,
      maxBlocks: 5,
      requiredBlockTypes: ['image-placeholder'],
    },
    tags: ['image', 'media', 'comparison', 'gallery'],
  }
);

/**
 * Image with Text Overlay Layout
 * Full-bleed image with a semi-transparent text panel on the bottom
 */
export const LAYOUT_IMAGE_TEXT_OVERLAY: LayoutTemplate = createLayoutTemplate(
  'image-text-overlay',
  'Image with Text Overlay',
  'media',
  [
    createSlot(
      'background-image',
      'Background Image',
      ['image-placeholder'],
      { x: 0, y: 0, width: W, height: H, unit: 'px' },
      {
        required: true,
        priority: 10,
        zIndex: 0,
      }
    ),
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: P, y: H * 0.55, width: W - P * 2, height: 70, unit: 'px' },
      {
        priority: 9,
        defaultStyle: {
          fontSize: 52,
          textAlignment: 'left',
          fontName: 'HelveticaNeue-Bold',
          textColor: '#FFFFFF',
        },
        constraints: { maxLines: 2 },
        zIndex: 2,
      }
    ),
    createSlot(
      'body',
      'Body Text',
      ['body', 'bullet-list', 'caption', 'stat', 'chart'],
      { x: P, y: H * 0.68, width: W - P * 2, height: H * 0.25, unit: 'px' },
      {
        priority: 7,
        defaultStyle: {
          fontSize: 28,
          textAlignment: 'left',
          fontName: 'HelveticaNeue',
          textColor: '#FFFFFF',
          lineHeight: 1.4,
        },
        constraints: { maxLines: 5 },
        zIndex: 2,
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Full-bleed image with text overlay at bottom',
    selectionRules: {
      preferredScreenTypes: ['image-focus', 'section-header'],
      minBlocks: 1,
      maxBlocks: 4,
      requiredBlockTypes: ['image-placeholder'],
    },
    tags: ['image', 'fullscreen', 'overlay', 'cinematic'],
  }
);

/**
 * Image Grid Layout
 * Title with 2x2 image grid below
 */
const GRID_GAP = 12;
const GRID_COLS = 2;
const GRID_AREA_W = W - P * 2;
const GRID_IMG_W = (GRID_AREA_W - GRID_GAP) / GRID_COLS;
const GRID_AREA_Y = 110;
const GRID_AREA_H = H - GRID_AREA_Y - P;
const GRID_IMG_H = (GRID_AREA_H - GRID_GAP) / 2;

export const LAYOUT_IMAGE_GRID: LayoutTemplate = createLayoutTemplate(
  'image-grid',
  'Image Grid',
  'media',
  [
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: P, y: P, width: W - P * 2, height: 55, unit: 'px' },
      {
        priority: 10,
        defaultStyle: {
          fontSize: 36,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'image-1',
      'Top Left Image',
      ['image-placeholder'],
      { x: P, y: GRID_AREA_Y, width: GRID_IMG_W, height: GRID_IMG_H, unit: 'px' },
      { required: true, priority: 9, zIndex: 0 }
    ),
    createSlot(
      'image-2',
      'Top Right Image',
      ['image-placeholder'],
      { x: P + GRID_IMG_W + GRID_GAP, y: GRID_AREA_Y, width: GRID_IMG_W, height: GRID_IMG_H, unit: 'px' },
      { priority: 8, zIndex: 0 }
    ),
    createSlot(
      'image-3',
      'Bottom Left Image',
      ['image-placeholder'],
      { x: P, y: GRID_AREA_Y + GRID_IMG_H + GRID_GAP, width: GRID_IMG_W, height: GRID_IMG_H, unit: 'px' },
      { priority: 7, zIndex: 0 }
    ),
    createSlot(
      'image-4',
      'Bottom Right Image',
      ['image-placeholder'],
      { x: P + GRID_IMG_W + GRID_GAP, y: GRID_AREA_Y + GRID_IMG_H + GRID_GAP, width: GRID_IMG_W, height: GRID_IMG_H, unit: 'px' },
      { priority: 6, zIndex: 0 }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: '2x2 image grid with title, ideal for portfolios and comparisons',
    selectionRules: {
      preferredScreenTypes: ['image-focus', 'comparison', 'team'],
      minBlocks: 2,
      maxBlocks: 6,
      requiredBlockTypes: ['image-placeholder'],
    },
    tags: ['image', 'grid', 'gallery', 'portfolio'],
  }
);

// =========================================================================
// Full-bleed & wide image layouts for content slides
// =========================================================================

/**
 * Full Image Content Layout
 * Full-bleed image background with title + body/bullets overlay.
 * ImageOverlayRule adds a 50% black shape; ContrastRule makes text white.
 */
export const LAYOUT_FULL_IMAGE_CONTENT: LayoutTemplate = createLayoutTemplate(
  'full-image-content',
  'Full Image Content',
  'media',
  [
    createSlot(
      'background-image',
      'Background Image',
      ['image-placeholder'],
      { x: 0, y: 0, width: W, height: H, unit: 'px' },
      {
        required: true,
        priority: 10,
        zIndex: 0,
      }
    ),
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: P, y: P, width: W - P * 2, height: 80, unit: 'px' },
      {
        priority: 9,
        defaultStyle: {
          fontSize: 52,
          textAlignment: 'left',
          fontName: 'HelveticaNeue-Bold',
          textColor: '#FFFFFF',
        },
        constraints: { maxLines: 2 },
        zIndex: 2,
      }
    ),
    createSlot(
      'body',
      'Body Text',
      ['body', 'bullet-list', 'numbered-list', 'stat', 'chart'],
      { x: P, y: 130, width: W - P * 2, height: H - 170, unit: 'px' },
      {
        priority: 8,
        defaultStyle: {
          fontSize: 30,
          textAlignment: 'left',
          fontName: 'HelveticaNeue',
          textColor: '#FFFFFF',
          lineHeight: 1.5,
        },
        constraints: { maxLines: 12 },
        zIndex: 2,
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Full-bleed image background with title and body overlay',
    selectionRules: {
      preferredScreenTypes: ['content', 'image-focus'],
      minBlocks: 1,
      maxBlocks: 4,
      requiredBlockTypes: ['image-placeholder'],
      scoreBonus: 1,
    },
    tags: ['image', 'fullscreen', 'content', 'overlay'],
  }
);

/**
 * Image Left Wide Layout
 * Edge-to-edge image fills left 60%, text in right 40%.
 */
export const LAYOUT_IMAGE_LEFT_WIDE: LayoutTemplate = createLayoutTemplate(
  'image-left-wide',
  'Image Left Wide',
  'media',
  [
    createSlot(
      'image',
      'Image',
      ['image-placeholder'],
      { x: 0, y: 0, width: Math.round(W * 0.6), height: H, unit: 'px' },
      {
        required: true,
        priority: 10,
        zIndex: 0,
      }
    ),
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      {
        x: Math.round(W * 0.6) + P,
        y: P,
        width: Math.round(W * 0.4) - P * 2,
        height: 80,
        unit: 'px',
      },
      {
        priority: 9,
        defaultStyle: {
          fontSize: 48,
          textAlignment: 'left',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'body',
      'Body Text',
      ['body', 'bullet-list', 'numbered-list', 'caption', 'stat', 'chart'],
      {
        x: Math.round(W * 0.6) + P,
        y: 130,
        width: Math.round(W * 0.4) - P * 2,
        height: H - 170,
        unit: 'px',
      },
      {
        priority: 7,
        defaultStyle: {
          fontSize: 28,
          textAlignment: 'left',
          fontName: 'HelveticaNeue',
          lineHeight: 1.4,
        },
        constraints: { maxLines: 12 },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Edge-to-edge image on left 60%, text on right 40%',
    selectionRules: {
      preferredScreenTypes: ['content', 'image-focus'],
      minBlocks: 1,
      maxBlocks: 4,
      requiredBlockTypes: ['image-placeholder'],
    },
    tags: ['image', 'media', 'split', 'wide'],
  }
);

/**
 * Image Right Wide Layout
 * Text in left 40%, edge-to-edge image fills right 60%.
 */
export const LAYOUT_IMAGE_RIGHT_WIDE: LayoutTemplate = createLayoutTemplate(
  'image-right-wide',
  'Image Right Wide',
  'media',
  [
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: P, y: P, width: Math.round(W * 0.4) - P * 2, height: 80, unit: 'px' },
      {
        priority: 9,
        defaultStyle: {
          fontSize: 48,
          textAlignment: 'left',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'body',
      'Body Text',
      ['body', 'bullet-list', 'numbered-list', 'caption', 'stat', 'chart'],
      { x: P, y: 130, width: Math.round(W * 0.4) - P * 2, height: H - 170, unit: 'px' },
      {
        priority: 7,
        defaultStyle: {
          fontSize: 28,
          textAlignment: 'left',
          fontName: 'HelveticaNeue',
          lineHeight: 1.4,
        },
        constraints: { maxLines: 12 },
      }
    ),
    createSlot(
      'image',
      'Image',
      ['image-placeholder'],
      { x: Math.round(W * 0.4), y: 0, width: Math.round(W * 0.6), height: H, unit: 'px' },
      {
        required: true,
        priority: 10,
        zIndex: 0,
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Text on left 40%, edge-to-edge image on right 60%',
    selectionRules: {
      preferredScreenTypes: ['content', 'image-focus'],
      minBlocks: 1,
      maxBlocks: 4,
      requiredBlockTypes: ['image-placeholder'],
    },
    tags: ['image', 'media', 'split', 'wide'],
  }
);

/**
 * Image Bottom Layout
 * Title + body at top, full-width image across the bottom 55%.
 */
export const LAYOUT_IMAGE_BOTTOM: LayoutTemplate = createLayoutTemplate(
  'image-bottom',
  'Image Bottom',
  'media',
  [
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: P, y: P, width: W - P * 2, height: 70, unit: 'px' },
      {
        priority: 10,
        defaultStyle: {
          fontSize: 48,
          textAlignment: 'left',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'body',
      'Body Text',
      ['body', 'bullet-list', 'numbered-list', 'caption', 'stat', 'chart'],
      { x: P, y: 120, width: W - P * 2, height: Math.round(H * 0.4) - 120, unit: 'px' },
      {
        priority: 8,
        defaultStyle: {
          fontSize: 28,
          textAlignment: 'left',
          fontName: 'HelveticaNeue',
          lineHeight: 1.4,
        },
        constraints: { maxLines: 6 },
      }
    ),
    createSlot(
      'image',
      'Image',
      ['image-placeholder'],
      { x: 0, y: Math.round(H * 0.45), width: W, height: Math.round(H * 0.55), unit: 'px' },
      {
        required: true,
        priority: 9,
        zIndex: 0,
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Title and body at top, full-width image at bottom',
    selectionRules: {
      preferredScreenTypes: ['content', 'image-focus'],
      minBlocks: 1,
      maxBlocks: 4,
      requiredBlockTypes: ['image-placeholder'],
    },
    tags: ['image', 'media', 'bottom'],
  }
);

/**
 * Image Background Card Layout
 * Full-bleed image with centered content card overlay.
 * ImageOverlayRule adds the dark overlay; text is white and centered.
 */
export const LAYOUT_IMAGE_BG_CARD: LayoutTemplate = createLayoutTemplate(
  'image-bg-card',
  'Image Background Card',
  'media',
  [
    createSlot(
      'background-image',
      'Background Image',
      ['image-placeholder'],
      { x: 0, y: 0, width: W, height: H, unit: 'px' },
      {
        required: true,
        priority: 10,
        zIndex: 0,
      }
    ),
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: 120, y: Math.round(H * 0.3), width: W - 240, height: 90, unit: 'px' },
      {
        priority: 9,
        defaultStyle: {
          fontSize: 52,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
          textColor: '#FFFFFF',
        },
        constraints: { maxLines: 2 },
        zIndex: 2,
      }
    ),
    createSlot(
      'body',
      'Body Text',
      ['body', 'bullet-list', 'subtitle', 'caption', 'stat', 'chart'],
      { x: 120, y: Math.round(H * 0.3) + 100, width: W - 240, height: 160, unit: 'px' },
      {
        priority: 7,
        defaultStyle: {
          fontSize: 30,
          textAlignment: 'center',
          fontName: 'HelveticaNeue',
          textColor: '#FFFFFF',
          lineHeight: 1.4,
        },
        constraints: { maxLines: 5 },
        zIndex: 2,
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Full-bleed image with centered content card overlay',
    selectionRules: {
      preferredScreenTypes: ['section-header', 'closing', 'content'],
      minBlocks: 1,
      maxBlocks: 4,
      requiredBlockTypes: ['image-placeholder'],
    },
    tags: ['image', 'fullscreen', 'card', 'overlay', 'centered'],
  }
);

/**
 * TOC (Table of Contents) Layout
 * Title at top with scrollable list area for TOC entries.
 * TOC entries are rendered as buttons by the compiler, not assigned to slots.
 */
export const LAYOUT_TOC: LayoutTemplate = createLayoutTemplate(
  'toc',
  'Table of Contents',
  'nav',
  [
    createSlot(
      'title',
      'Title',
      ['title'],
      { x: P, y: P, width: W - P * 2, height: 80, unit: 'px' },
      {
        required: true,
        priority: 10,
        defaultStyle: {
          fontSize: 48,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'toc-list',
      'TOC List',
      ['toc-list'],
      { x: P, y: 120, width: W - P * 2, height: H - 160, unit: 'px' },
      {
        priority: 9,
        // No defaultStyle - toc-list is handled specially by the compiler
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Table of Contents with clickable navigation entries',
    selectionRules: {
      preferredScreenTypes: ['toc'],
      minBlocks: 1,
      maxBlocks: 2,
      requiredBlockTypes: ['toc-list'],
    },
    tags: ['nav', 'toc', 'navigation'],
  }
);

/**
 * Timeline Layout
 * Horizontal timeline with events alternating above/below the line.
 * Timeline entries are rendered specially by the compiler.
 */
export const LAYOUT_TIMELINE: LayoutTemplate = createLayoutTemplate(
  'timeline',
  'Timeline',
  'content',
  [
    createSlot(
      'title',
      'Title',
      ['title'],
      { x: P, y: P, width: W - P * 2, height: 60, unit: 'px' },
      {
        required: false,
        priority: 10,
        defaultStyle: {
          fontSize: 36,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 2 },
      }
    ),
    createSlot(
      'timeline',
      'Timeline',
      ['timeline'],
      { x: P, y: 100, width: W - P * 2, height: H - 140, unit: 'px' },
      {
        priority: 9,
        // No defaultStyle - timeline is handled specially by the compiler
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Horizontal timeline with alternating event cards',
    selectionRules: {
      preferredScreenTypes: ['timeline'],
      minBlocks: 1,
      maxBlocks: 2,
      requiredBlockTypes: ['timeline'],
    },
    tags: ['timeline', 'chronological', 'events', 'history'],
  }
);

/**
 * Big Stat Layout
 * Single prominent stat centered on screen - for KPIs and key metrics
 */
export const LAYOUT_BIG_STAT: LayoutTemplate = createLayoutTemplate(
  'big-stat',
  'Big Stat',
  'content',
  [
    createSlot(
      'stat',
      'Main Stat',
      ['stat'],
      { x: P, y: H * 0.25, width: W - P * 2, height: H * 0.5, unit: 'px' },
      {
        required: true,
        priority: 10,
        defaultStyle: {
          fontSize: 120,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
      }
    ),
    createSlot(
      'title',
      'Title',
      ['title', 'subtitle'],
      { x: P, y: P, width: W - P * 2, height: 60, unit: 'px' },
      {
        priority: 5,
        defaultStyle: {
          fontSize: 32,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Medium',
        },
        constraints: { maxLines: 1 },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Single large stat centered for maximum impact',
    selectionRules: {
      preferredScreenTypes: ['big-stat'],
      minBlocks: 1,
      maxBlocks: 2,
      requiredBlockTypes: ['stat'],
      scoreBonus: 3,
    },
    tags: ['stat', 'kpi', 'metric', 'infographic', 'data'],
  }
);

/**
 * Stats Grid Layout
 * 2-4 stats arranged in a grid - for dashboards and KPI overviews
 */
export const LAYOUT_STATS_GRID: LayoutTemplate = createLayoutTemplate(
  'stats-grid',
  'Stats Grid',
  'content',
  [
    createSlot(
      'title',
      'Title',
      ['title'],
      { x: P, y: P, width: W - P * 2, height: 60, unit: 'px' },
      {
        priority: 10,
        defaultStyle: {
          fontSize: 40,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 1 },
      }
    ),
    createSlot(
      'stat-1',
      'Stat 1',
      ['stat'],
      { x: P, y: 120, width: (W - P * 3) / 2, height: (H - 180) / 2, unit: 'px' },
      {
        priority: 9,
        defaultStyle: {
          fontSize: 64,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
      }
    ),
    createSlot(
      'stat-2',
      'Stat 2',
      ['stat'],
      { x: P + (W - P * 3) / 2 + P, y: 120, width: (W - P * 3) / 2, height: (H - 180) / 2, unit: 'px' },
      {
        priority: 8,
        defaultStyle: {
          fontSize: 64,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
      }
    ),
    createSlot(
      'stat-3',
      'Stat 3',
      ['stat'],
      { x: P, y: 120 + (H - 180) / 2 + P, width: (W - P * 3) / 2, height: (H - 180) / 2, unit: 'px' },
      {
        priority: 7,
        defaultStyle: {
          fontSize: 64,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
      }
    ),
    createSlot(
      'stat-4',
      'Stat 4',
      ['stat'],
      { x: P + (W - P * 3) / 2 + P, y: 120 + (H - 180) / 2 + P, width: (W - P * 3) / 2, height: (H - 180) / 2, unit: 'px' },
      {
        priority: 6,
        defaultStyle: {
          fontSize: 64,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Grid of 2-4 stats for dashboard-style overviews',
    selectionRules: {
      preferredScreenTypes: ['stats-grid'],
      minBlocks: 2,
      maxBlocks: 5,
      scoreBonus: 2,
    },
    tags: ['stats', 'grid', 'dashboard', 'kpi', 'metrics', 'infographic'],
  }
);

/**
 * Chart Focus Layout
 * Full-screen chart with optional title - for data visualization
 */
export const LAYOUT_CHART_FOCUS: LayoutTemplate = createLayoutTemplate(
  'chart-focus',
  'Chart Focus',
  'content',
  [
    createSlot(
      'title',
      'Title',
      ['title'],
      { x: P, y: P, width: W - P * 2, height: 60, unit: 'px' },
      {
        priority: 8,
        defaultStyle: {
          fontSize: 36,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 1 },
      }
    ),
    createSlot(
      'chart',
      'Chart',
      ['chart'],
      { x: P, y: 100, width: W - P * 2, height: H - 140, unit: 'px' },
      {
        required: true,
        priority: 10,
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Full-screen chart for data visualization',
    selectionRules: {
      preferredScreenTypes: ['chart-focus'],
      minBlocks: 1,
      maxBlocks: 2,
      requiredBlockTypes: ['chart'],
      scoreBonus: 3,
    },
    tags: ['chart', 'data', 'visualization', 'infographic'],
  }
);

/**
 * Stats + Chart Combo Layout
 * Stats on one side, chart on the other - for data storytelling
 */
export const LAYOUT_STATS_CHART: LayoutTemplate = createLayoutTemplate(
  'stats-chart',
  'Stats + Chart',
  'content',
  [
    createSlot(
      'title',
      'Title',
      ['title'],
      { x: P, y: P, width: W - P * 2, height: 60, unit: 'px' },
      {
        priority: 10,
        defaultStyle: {
          fontSize: 36,
          textAlignment: 'left',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 1 },
      }
    ),
    createSlot(
      'stat-1',
      'Stat 1',
      ['stat'],
      { x: P, y: 100, width: (W - P * 3) * 0.35, height: (H - 140) / 2 - P / 2, unit: 'px' },
      {
        priority: 8,
        defaultStyle: {
          fontSize: 48,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
      }
    ),
    createSlot(
      'stat-2',
      'Stat 2',
      ['stat'],
      { x: P, y: 100 + (H - 140) / 2 + P / 2, width: (W - P * 3) * 0.35, height: (H - 140) / 2 - P / 2, unit: 'px' },
      {
        priority: 7,
        defaultStyle: {
          fontSize: 48,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
      }
    ),
    createSlot(
      'chart',
      'Chart',
      ['chart'],
      { x: P + (W - P * 3) * 0.35 + P, y: 100, width: (W - P * 3) * 0.65, height: H - 140, unit: 'px' },
      {
        priority: 9,
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Stats on left with chart on right for data storytelling',
    selectionRules: {
      preferredScreenTypes: ['stats-grid', 'chart-focus'],
      minBlocks: 2,
      maxBlocks: 4,
    },
    tags: ['stats', 'chart', 'combo', 'data', 'infographic'],
  }
);

/**
 * Three Stats Row Layout
 * Three stats in a horizontal row - clean and balanced
 */
export const LAYOUT_THREE_STATS: LayoutTemplate = createLayoutTemplate(
  'three-stats',
  'Three Stats Row',
  'content',
  [
    createSlot(
      'title',
      'Title',
      ['title'],
      { x: P, y: P, width: W - P * 2, height: 70, unit: 'px' },
      {
        priority: 10,
        defaultStyle: {
          fontSize: 40,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
        constraints: { maxLines: 1 },
      }
    ),
    createSlot(
      'stat-1',
      'Stat 1',
      ['stat'],
      { x: P, y: H * 0.25, width: (W - P * 4) / 3, height: H * 0.5, unit: 'px' },
      {
        priority: 9,
        defaultStyle: {
          fontSize: 56,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
      }
    ),
    createSlot(
      'stat-2',
      'Stat 2',
      ['stat'],
      { x: P + (W - P * 4) / 3 + P, y: H * 0.25, width: (W - P * 4) / 3, height: H * 0.5, unit: 'px' },
      {
        priority: 8,
        defaultStyle: {
          fontSize: 56,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
      }
    ),
    createSlot(
      'stat-3',
      'Stat 3',
      ['stat'],
      { x: P + 2 * ((W - P * 4) / 3 + P), y: H * 0.25, width: (W - P * 4) / 3, height: H * 0.5, unit: 'px' },
      {
        priority: 7,
        defaultStyle: {
          fontSize: 56,
          textAlignment: 'center',
          fontName: 'HelveticaNeue-Bold',
        },
      }
    ),
    createSlot(
      'notes',
      'Speaker Notes',
      ['speaker-notes'],
      { x: 0, y: 0, width: 0, height: 0, unit: 'px' },
      { priority: 0, zIndex: -1 }
    ),
  ],
  {
    description: 'Three stats in a balanced horizontal row',
    selectionRules: {
      preferredScreenTypes: ['stats-grid'],
      minBlocks: 3,
      maxBlocks: 4,
    },
    tags: ['stats', 'row', 'three', 'metrics', 'infographic'],
  }
);

/**
 * All predefined layouts in a map for easy lookup
 */
export const PREDEFINED_LAYOUTS: Map<string, LayoutTemplate> = new Map([
  [LAYOUT_TITLE_CENTERED.id, LAYOUT_TITLE_CENTERED],
  [LAYOUT_TITLE_BODY.id, LAYOUT_TITLE_BODY],
  [LAYOUT_TWO_COLUMN.id, LAYOUT_TWO_COLUMN],
  [LAYOUT_IMAGE_LEFT.id, LAYOUT_IMAGE_LEFT],
  [LAYOUT_IMAGE_RIGHT.id, LAYOUT_IMAGE_RIGHT],
  [LAYOUT_FULL_IMAGE.id, LAYOUT_FULL_IMAGE],
  [LAYOUT_IMAGE_TOP.id, LAYOUT_IMAGE_TOP],
  [LAYOUT_TWO_IMAGES.id, LAYOUT_TWO_IMAGES],
  [LAYOUT_IMAGE_TEXT_OVERLAY.id, LAYOUT_IMAGE_TEXT_OVERLAY],
  [LAYOUT_IMAGE_GRID.id, LAYOUT_IMAGE_GRID],
  [LAYOUT_BULLETS.id, LAYOUT_BULLETS],
  [LAYOUT_QUOTE.id, LAYOUT_QUOTE],
  [LAYOUT_THREE_COLUMN.id, LAYOUT_THREE_COLUMN],
  [LAYOUT_CLOSING.id, LAYOUT_CLOSING],
  [LAYOUT_VERTICAL_MOBILE.id, LAYOUT_VERTICAL_MOBILE],
  [LAYOUT_FULL_IMAGE_CONTENT.id, LAYOUT_FULL_IMAGE_CONTENT],
  [LAYOUT_IMAGE_LEFT_WIDE.id, LAYOUT_IMAGE_LEFT_WIDE],
  [LAYOUT_IMAGE_RIGHT_WIDE.id, LAYOUT_IMAGE_RIGHT_WIDE],
  [LAYOUT_IMAGE_BOTTOM.id, LAYOUT_IMAGE_BOTTOM],
  [LAYOUT_IMAGE_BG_CARD.id, LAYOUT_IMAGE_BG_CARD],
  [LAYOUT_TOC.id, LAYOUT_TOC],
  [LAYOUT_TIMELINE.id, LAYOUT_TIMELINE],
  [LAYOUT_BIG_STAT.id, LAYOUT_BIG_STAT],
  [LAYOUT_STATS_GRID.id, LAYOUT_STATS_GRID],
  [LAYOUT_CHART_FOCUS.id, LAYOUT_CHART_FOCUS],
  [LAYOUT_STATS_CHART.id, LAYOUT_STATS_CHART],
  [LAYOUT_THREE_STATS.id, LAYOUT_THREE_STATS],
]);

/**
 * All predefined layouts as an array
 */
export const ALL_LAYOUTS: LayoutTemplate[] = Array.from(
  PREDEFINED_LAYOUTS.values()
);

/**
 * Get layouts by category
 */
export function getLayoutsByCategory(
  category: LayoutTemplate['category']
): LayoutTemplate[] {
  return ALL_LAYOUTS.filter((layout) => layout.category === category);
}

/**
 * Get layouts by tags
 */
export function getLayoutsByTags(tags: string[]): LayoutTemplate[] {
  return ALL_LAYOUTS.filter((layout) =>
    tags.some((tag) => layout.tags?.includes(tag))
  );
}

/**
 * Get a layout by ID
 */
export function getLayoutById(id: string): LayoutTemplate | undefined {
  return PREDEFINED_LAYOUTS.get(id);
}

/**
 * Default layout to use when no better match is found
 */
export const DEFAULT_LAYOUT = LAYOUT_TITLE_BODY;
